if( !customElements.get( 'ormu-product-addons' ) ){
	customElements.define(
		'ormu-product-addons',
		class OrmuProductAddons extends HTMLElement{
			constructor(){
				super();
				this._onSubmit = this._onSubmit.bind( this );
				this._onVariantChange = this._onVariantChange.bind( this );
				this._productInfo = null;
				this._form = null;
				this._unsubscribeVariantChange = null;
			}

			connectedCallback(){
				this._productInfo = this.closest( 'product-info' );
				this._form = this._resolveProductForm();
				this._form?.addEventListener( 'submit', this._onSubmit, true );
				this._unsubscribeVariantChange = theme.pubsub.subscribe(
					theme.pubsub.PUB_SUB_EVENTS.variantChange,
					this._onVariantChange
				);
				this._syncContainerVisibility();
			}

			disconnectedCallback(){
				this._form?.removeEventListener( 'submit', this._onSubmit, true );
				this._unsubscribeVariantChange?.();
			}

			_resolveProductForm(){
				const formId = this._productInfo?.getAttribute( 'form' );
				return formId ? document.getElementById( formId ) : this._productInfo?.querySelector( 'form[action*="/cart/add"]' );
			}

			_getSelectedAddons( mainQuantity = 1, parentId ){
				const selected = new Map();

				this.querySelectorAll( 'ormu-product-addon' ).forEach( ( addon ) => {
					const input = addon.querySelector( '[data-ormu-addon-input]' );
					const isMandatory = addon.dataset.mandatory === 'true';

					if( !isMandatory && !input?.checked ) return;

					const id = Number.parseInt( addon.dataset.variantId, 10 );
					const configuredQuantity = Number.parseInt( addon.dataset.quantity, 10 );

					if( Number.isNaN( id ) ) return;

					const quantity = ( Number.isNaN( configuredQuantity ) || configuredQuantity < 1 ? 1 : configuredQuantity ) * mainQuantity;
					selected.set( id, ( selected.get( id ) || 0 ) + quantity );
				} );

				return Array.from( selected, ( [ id, quantity ] ) => ( { id, parent_id: parentId, quantity } ) );
			}

			_extractMainItem( formData ){
				const id = Number.parseInt( formData.get( 'id' ), 10 );
				if( Number.isNaN( id ) ) return null;

				const parsedQuantity = Number.parseInt( formData.get( 'quantity' ), 10 );
				const item = {
					id,
					quantity: Number.isNaN( parsedQuantity ) || parsedQuantity < 1 ? 1 : parsedQuantity
				};
				const sellingPlan = formData.get( 'selling_plan' );

				if( sellingPlan ){
					item.selling_plan = sellingPlan;
				}

				const properties = {};
				formData.forEach( ( value, key ) => {
					if( !key.startsWith( 'properties[' ) || !key.endsWith( ']' ) ) return;

					const propertyKey = key.slice( 11, -1 );
					if( propertyKey ) properties[ propertyKey ] = value;
				} );

				if( Object.keys( properties ).length > 0 ){
					item.properties = properties;
				}

				return item;
			}

			_getSubmitButtons( form, submitter ){
				const buttons = new Set( form.querySelectorAll( '[type="submit"]' ) );
				document.querySelectorAll( `[type="submit"][form="${ form.id }"]` ).forEach( ( button ) => buttons.add( button ) );
				if( submitter ) buttons.add( submitter );
				return Array.from( buttons );
			}

			async _onSubmit( event ){
				if( event.defaultPrevented ) return;

				const form = event.currentTarget;
				const formData = new FormData( form );
				const mainItem = this._extractMainItem( formData );
				if( !mainItem ) return;

				const addonItems = this._getSelectedAddons( mainItem.quantity, mainItem.id );
				if( addonItems.length === 0 ) return;

				event.preventDefault();
				event.stopImmediatePropagation();

				const submitButtons = this._getSubmitButtons( form, event.submitter );
				if( submitButtons.some( ( button ) => button.getAttribute( 'aria-busy' ) === 'true' ) ) return;

				if( !form.checkValidity() ){
					form.reportValidity();
					return;
				}

				form.handleErrorMessage?.();
				submitButtons.forEach( ( button ) => {
					button.setAttribute( 'aria-disabled', 'true' );
					button.setAttribute( 'aria-busy', 'true' );
				} );

				const sections = [];
				document.documentElement.dispatchEvent( new CustomEvent( 'cart:bundled-sections', {
					bubbles: true,
					detail: { sections }
				} ) );

				const payload = {
					items: [ mainItem, ...addonItems ],
					sections,
					sections_url: window.location.pathname
				};

				try{
					const config = theme.utils.fetchConfig( 'javascript' );
					config.headers[ 'X-Requested-With' ] = 'XMLHttpRequest';
					config.body = JSON.stringify( payload );

					const response = await fetch( theme.routes.cart_add_url, config );
					const responseJson = await response.json();

					if( !response.ok || responseJson.status ) throw responseJson;

					if( theme.settings.cartType === 'page' || theme.settings.isCartTemplate ){
						window.location.href = theme.routes.cart_url;
						return;
					}

					const cartResponse = await fetch( theme.routes.cart_url, theme.utils.fetchConfig( 'json', 'GET' ) );
					const cart = await cartResponse.json();
					cart.sections = responseJson.sections || {};

					theme.pubsub.publish( theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
						source: 'product-form',
						productVariantId: mainItem.id,
						cart
					} );
					document.dispatchEvent( new CustomEvent( 'ajaxProduct:added', {
						detail: { product: responseJson }
					} ) );
					document.querySelector( 'cart-drawer' )?.show( event.submitter || form );
				}
				catch( error ){
					const message = error?.description || error?.message || theme.cartStrings.error;

					theme.pubsub.publish( theme.pubsub.PUB_SUB_EVENTS.cartError, {
						source: 'product-form',
						productVariantId: mainItem.id,
						errors: message,
						message
					} );
					form.handleErrorMessage?.( message );
					document.dispatchEvent( new CustomEvent( 'ajaxProduct:error', {
						detail: { errorMessage: message }
					} ) );
					document.dispatchEvent( new CustomEvent( 'cart:refresh', {
						bubbles: true,
						detail: { open: false }
					} ) );
				}
				finally{
					submitButtons.forEach( ( button ) => {
						button.removeAttribute( 'aria-busy' );
						button.removeAttribute( 'aria-disabled' );
					} );
				}
			}

			_onVariantChange( event ){
				const data = event?.data;
				if( String( data?.sectionId ) !== this._productInfo?.dataset.sectionId || String( data?.productId ) !== this.dataset.productId ) return;

				const source = data.parsedHTML?.getElementById( this.id );
				if( !source ) return;

				const selectedProductIds = new Set(
					Array.from( this.querySelectorAll( 'ormu-product-addon' ) )
						.filter( ( addon ) => addon.dataset.mandatory !== 'true' )
						.filter( ( addon ) => addon.querySelector( '[data-ormu-addon-input]' )?.checked )
						.map( ( addon ) => addon.dataset.productId )
				);
				const replacement = source.cloneNode( true );

				replacement.querySelectorAll( 'ormu-product-addon' ).forEach( ( addon ) => {
					if( addon.dataset.mandatory === 'true' ) return;
					const input = addon.querySelector( '[data-ormu-addon-input]' );
					if( input ) input.checked = selectedProductIds.has( addon.dataset.productId );
				} );

				this.replaceWith( replacement );
			}

			_syncContainerVisibility(){
				const container = this.parentElement?.matches( '.rte' ) ? this.parentElement : null;
				container?.toggleAttribute( 'hidden', this.dataset.hasAddons !== 'true' );
			}
		}
	);
}
